import { useEffect, useState, useRef, useCallback } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Stack,
  Typography,
  TextField,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import DialpadIcon from "@mui/icons-material/Dialpad";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import QrSDK from "lu-sdk";
import QRCodeGen from "qrcode";
import io from "socket.io-client";
import Inputfield from "./Inputfield";
import dummyuser from "../assets/profile_placeholder.jpg";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import playstore from "../assets/playstore.png";
import appstore from "../assets/appstore.png";
import lulogo from "../assets/LU-2026--Main-Revised.png";
import { toast } from "react-toastify";

const socket = io("https://lu-api.legitimatelyu.com", {
  transports: ["polling"], // Force polling only to prevent Nginx WebSocket upgrade errors
});

const hideScrollbarSx = {
  overflowY: "auto",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
};


// ─── LU Result Popup ────────────────────────────────────────────────────────
const LUResultDialog = ({ open, type, onClose }) => {
  const isSuccess = type === "success";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Fade}
      transitionDuration={300}
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 2, sm: 3 },
            p: 0,
            m: { xs: 2, sm: 2 },
            width: "100%",
            maxWidth: 340,
            overflow: "hidden",
            boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          },
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 4,
          pb: 3,
          px: 3,
          textAlign: "center",
          background: isSuccess
            ? "linear-gradient(160deg, #f0fff4 0%, #ffffff 100%)"
            : "linear-gradient(160deg, #fff5f5 0%, #ffffff 100%)",
        }}
      >
        {/* Icon badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: 1,
            bgcolor: isSuccess ? "#e6fbf0" : "#fde8e8",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2.5,
            fontSize: 30,
            boxShadow: isSuccess
              ? "0 0 0 10px rgba(82,196,26,0.08)"
              : "0 0 0 10px rgba(220,38,38,0.08)",
          }}
        >
          {isSuccess ? "✓" : "✕"}
        </Box>

        {/* Message */}
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: 16, sm: 18 },
            lineHeight: 1.4,
            color: "#1a1a2e",
            mb: 1,
          }}
        >
          {isSuccess ? (
            <>
              Identity verified by{" "}
              <Box component="span" sx={{ color: "#dc2626", fontWeight: 800 }}>
                LU
              </Box>
            </>
          ) : (
            <>
              <Box component="span" sx={{ color: "#dc2626", fontWeight: 800 }}>
                LU
              </Box>{" "}
              verification failed. Please try again
            </>
          )}
        </Typography>



        <Button
          variant="contained"
          onClick={onClose}
          disableElevation
          sx={{
            borderRadius: 1,
            px: 4,
            py: 1,
            mt: 2,
            fontWeight: 700,
            fontSize: 14,
            bgcolor: isSuccess ? "#274bb5" : "#dc2626",
            color: "#fff",
            textTransform: "none",
            "&:hover": {
              bgcolor: isSuccess ? "#1d3996" : "#b91c1c",
            },
          }}
        >
          {isSuccess ? "Continue" : "Try Again"}
        </Button>
      </Box>
    </Dialog>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const QrLogin = ({ onLogin, onSkip, setMainData }) => {
  const [qrCode, setQrCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [isLoginModal, setIsLoginModal] = useState(false);
  const [data, setData] = useState("");
  const [loadingInput, setLoadingInput] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [activeTab, setActiveTab] = useState("qr");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);
  const [luPopup, setLuPopup] = useState({ open: false, type: "success" });

  const openLuPopup = useCallback((type) => setLuPopup({ open: true, type }), []);
  const closeLuPopup = useCallback(() => {
    setLuPopup((p) => {
      if (p.type === "failure") {
        setOtp(["", "", "", "", "", ""]);
        setTimeout(() => otpRefs.current[0]?.focus(), 0);
      }
      return { ...p, open: false };
    });
  }, []);

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    if (!email || !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    const otpString = otp.join("");
    if (otpString.length !== 6) {
      toast.error("Please enter a 6-digit OTP");
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await axios.post("https://api.legitimatelyu.com/api/v1/user/totp/verify",
        {
          email: email.trim(),
          totp: otpString
        }
      );

      if (response.data.success && response.data.result) {
        openLuPopup("success");
        // The backend returns { verified: true, data: { user: {...} } } in result, 
        // OR the full login response. We just reuse the existing data flow:
        setData(response.data.result);
      } else {
        openLuPopup("failure");
      }
    } catch (err) {
      openLuPopup("failure");
    } finally {
      setVerifyingOtp(false);
    }
  };

  const roomIdRef = useRef(uuidv4());

  useEffect(() => {
    if (socket.connected) {
      socket.emit("join_room", roomIdRef.current);
    } else {
      socket.on("connect", () => {
        socket.emit("join_room", roomIdRef.current);
      });
    }
    return () => {
      socket.off("connect");
    };
  }, []);

  const handleAuth = (resp) => {
    setData(resp);
  };

  useEffect(() => {
    socket.on("authentication", handleAuth);
    return () => {
      socket.off("authentication", handleAuth);
    };
  }, []);

  const initializeLuSdk = async () => {
    try {
      new QrSDK("8275530", "$2a$10$dZa7eAosabPc07URfAxKducgVBWdtccWcHLLxErnizEaTm93z5o6e");

      const custom = JSON.stringify({
        id: "1a3153ea-30b2-49da-bf2c-5cace77ed723",
        room_id: roomIdRef.current,
        client_id: "2307586",
      });

      const payload = { id: custom };

      QRCodeGen.toDataURL(JSON.stringify(payload), function (err, code) {
        if (err) throw err;
        setQrCode(code);
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    initializeLuSdk();
  }, [onLogin]);

  useEffect(() => {
    if (data) {
      if (typeof data?.user === "object" && !Array.isArray(data?.user) && data?.user?.is_authenticated === true) {
        setLoading(true);
        localStorage.setItem("Authenticated", "true");
        localStorage.setItem("data", JSON.stringify(data));
        setMainData(data);
        setIsLoginModal(true);
        const interval = setInterval(() => {
          setLoading(false);
          setLoadingInput(false);
        }, 2000);
        return () => clearInterval(interval);
      } else {
        setLoading(false);
        openLuPopup("failure");
        setLoadingInput(false);
      }
    }
  }, [data, setMainData, openLuPopup]);

  const handleCloseModal = () => {
    onSkip();
    setIsLoginModal(false);
    setLoadingInput(false);
    setData("");
    setOtp(["", "", "", "", "", ""]);
  };

  return (
    <>
      <LUResultDialog open={luPopup.open} type={luPopup.type} onClose={closeLuPopup} />
      <Dialog
        open={onLogin}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        slotProps={{
          paper: {
            sx: {
              m: { xs: 1, sm: 2 },
              width: { xs: "calc(100% - 16px)", sm: "100%" },
              maxWidth: { xs: "100%", sm: 480 },
              maxHeight: {
                xs: "calc(100dvh - 16px - env(safe-area-inset-top) - env(safe-area-inset-bottom))",
                sm: "calc(100vh - 48px)",
              },
              display: "flex",
              flexDirection: "column",
              borderRadius: { xs: 2, sm: 3 },
            },
          },
        }}
      >
        {!isLoginModal && (
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 1,
              py: { xs: 1.25, sm: 2 },
              px: { xs: 2, sm: 3 },
              fontSize: { xs: "1.05rem", sm: "1.25rem" },
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            Secure Identity Verification
            <IconButton aria-label="close" onClick={handleCloseModal} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
        )}
        {isLoginModal && (
          <IconButton aria-label="close" onClick={handleCloseModal} sx={{ position: "absolute", right: 8, top: 8, zIndex: 1 }}>
            <CloseIcon />
          </IconButton>
        )}
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: 0.5, sm: 1 },
            pb: { xs: 2, sm: 3 },
            flex: 1,
            minHeight: 0,
            textAlign: "center",
            ...hideScrollbarSx,
          }}
        >
          {loading ? (
            <Stack sx={{ alignItems: "center", justifyContent: "center", height: { xs: 220, sm: 300 }, gap: 2 }}>
              <CircularProgress size={48} />
              <Typography color="text.secondary" sx={{ fontSize: { xs: 13, sm: 14 } }}>
                Generating QR Code...
              </Typography>
            </Stack>
          ) : (
            <>
              {!isLoginModal ? (
                <Box sx={{ width: "100%", textAlign: "center" }}>
                  <Stack spacing={{ xs: 2.5, sm: 3 }} sx={{ width: "100%", px: { xs: 0.5, sm: 1 } }}>
                    <Box sx={{ width: '100%', gap: 0.75, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Button
                        onClick={() => {
                          const clientId = "2307586";
                          const redirectUri = window.location.origin + window.location.pathname;
                          const authUrl = `https://web.legitimatelyu.com/?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}`;
                          window.open(authUrl, "_blank");
                        }}
                        sx={{
                          width: "100%",
                          px: { xs: 2, sm: 3 },
                          py: { xs: 1, sm: 1.25 },
                          bgcolor: "#ffeb00",
                          color: "#000",
                          fontWeight: 700,
                          fontSize: { xs: 13, sm: 14 },
                          borderRadius: 2,
                          boxShadow: "0 4px 10px rgba(255,252,0,0.3)",
                          "&:hover": { bgcolor: "#ffff33", transform: "translateY(-1px)" },
                        }}
                      >
                        <Box component="img" src={lulogo} alt="LU" sx={{ width: { xs: 20, sm: 22 }, height: { xs: 20, sm: 22 }, borderRadius: 0.5, mr: 1 }} />
                        Authorize with Legitimately U
                      </Button>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        align="center"
                        sx={{
                          fontStyle: "italic",
                          textAlign: "center",
                          fontSize: { xs: 11, sm: 12 },
                          lineHeight: 1.45,
                          width: "100%",
                          display: "block",
                          mt: 1
                        }}
                      >
                        Initiate a secure biometric handshake with your LU Mobile App<br />for instant verification.
                      </Typography>
                    </Box>

                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, mb: 1.5, textAlign: 'left', width: '100%', color: 'text.primary' }}>
                        Choose a verification method
                      </Typography>
                      <Stack direction="row" spacing={1.5} sx={{ width: '100%', mb: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={() => setActiveTab('qr')}
                          sx={{
                            flex: 1,
                            height: 64,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            textTransform: 'none',
                            border: '1px solid',
                            borderColor: activeTab === 'qr' ? '#2f54eb' : 'divider',
                            borderWidth: activeTab === 'qr' ? 1.5 : 1,
                            bgcolor: activeTab === 'qr' ? '#f0f5ff' : 'transparent',
                            color: activeTab === 'qr' ? '#2f54eb' : 'text.primary',
                            p: 1,
                            '&:hover': { borderColor: '#2f54eb', bgcolor: '#f0f5ff', color: '#2f54eb' }
                          }}
                        >
                          <QrCodeScannerIcon sx={{ fontSize: 24 }} />
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'inherit' }}>
                              Scan QR Code
                            </Typography>
                          </Box>
                        </Button>

                        <Button
                          variant="outlined"
                          onClick={() => setActiveTab('otp')}
                          sx={{
                            flex: 1,
                            height: 64,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1.5,
                            textTransform: 'none',
                            border: '1px solid',
                            borderColor: activeTab === 'otp' ? '#2f54eb' : 'divider',
                            borderWidth: activeTab === 'otp' ? 1.5 : 1,
                            bgcolor: activeTab === 'otp' ? '#f0f5ff' : 'transparent',
                            color: activeTab === 'otp' ? '#2f54eb' : 'text.primary',
                            p: 1,
                            '&:hover': { borderColor: '#2f54eb', bgcolor: '#f0f5ff', color: '#2f54eb' }
                          }}
                        >
                          <DialpadIcon sx={{ fontSize: 24 }} />
                          <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'inherit' }}>
                            Verification Code
                          </Typography>
                        </Button>
                      </Stack>

                      {activeTab === 'qr' ? (
                        <Box sx={{ width: '100%', height: { xs: 'auto', sm: 345 }, bgcolor: 'action.hover', borderRadius: 3, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Box sx={{ position: 'relative', p: 3.5, mb: 2 }}>
                            <Box sx={{ position: 'absolute', top: 0, left: 0, width: 28, height: 28, borderTop: '1px solid #829fff', borderLeft: '1px solid #829fff', borderTopLeftRadius: 12 }} />
                            <Box sx={{ position: 'absolute', top: 0, right: 0, width: 28, height: 28, borderTop: '1px solid #829fff', borderRight: '1px solid #829fff', borderTopRightRadius: 12 }} />
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, width: 28, height: 28, borderBottom: '1px solid #829fff', borderLeft: '1px solid #829fff', borderBottomLeftRadius: 12 }} />
                            <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 28, height: 28, borderBottom: '1px solid #829fff', borderRight: '1px solid #829fff', borderBottomRightRadius: 12 }} />

                            <Box sx={{
                              width: 170,
                              height: 170,
                              bgcolor: '#fff',
                              borderRadius: '36px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 24px rgba(0,0,0,0.06)'
                            }}>
                              <Box
                                component="img"
                                src={qrCode}
                                alt="Login QR Code"
                                sx={{ width: 130, height: 130 }}
                              />
                            </Box>
                          </Box>
                          <Typography
                            align="center"
                            sx={{ textAlign: "center", fontSize: { xs: 12, sm: 13 }, fontWeight: 500, color: 'text.secondary', lineHeight: 1.5, maxWidth: 290 }}
                          >
                            Open the Legitimately U app on your phone and scan this secure token to sync your session.
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ width: '100%', height: { xs: 'auto', sm: 345 }, bgcolor: 'action.hover', borderRadius: 3, p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <Stack direction="column" sx={{ width: '100%', mb: 3 }}>
                            <Box sx={{ width: '100%', px: 1, display: 'flex', flexDirection: 'column' }}>
                              <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary', mb: 0.75, textAlign: 'left' }}>Email Address</Typography>
                              <TextField
                                fullWidth
                                placeholder="user@example.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: 999,
                                    bgcolor: 'background.paper',
                                  }
                                }}
                              />
                            </Box>

                            <Divider sx={{ width: '100%', my: 2.5, borderColor: 'rgba(0,0,0,0.06)' }} />

                            <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", mb: 1.5 }}>
                                <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary', textAlign: 'left', m: 0 }}>
                                  Enter Verification Code from your LU Mobile App
                                </Typography>
                              </Stack>
                              <Stack direction="row" spacing={{ xs: 1, sm: 2 }} sx={{ justifyContent: 'center', width: '100%' }}>
                                {[0, 1, 2, 3, 4, 5].map((idx) => (
                                  <TextField
                                    key={idx}
                                    inputRef={el => otpRefs.current[idx] = el}
                                    value={otp[idx]}
                                    onChange={(e) => handleOtpChange(idx, e.target.value)}
                                    onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                                    slotProps={{ htmlInput: { maxLength: 1, style: { textAlign: 'center', fontSize: '24px', fontWeight: '400', padding: 0, height: '100%' } } }}
                                    sx={{
                                      width: { xs: 42, sm: 52 },
                                      height: { xs: 46, sm: 56 },
                                      borderRadius: '12px',
                                      '& .MuiOutlinedInput-root': {
                                        height: '100%',
                                        bgcolor: 'background.paper',
                                        borderRadius: '12px',
                                        '&.Mui-focused fieldset': { borderColor: '#2f54eb', borderWidth: '1px' },
                                      }
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          </Stack>

                          <Button
                            variant="contained"
                            fullWidth
                            onClick={handleVerifyOtp}
                            disabled={verifyingOtp}
                            endIcon={verifyingOtp ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
                            sx={{
                              bgcolor: '#274bb5',
                              color: '#fff',
                              fontWeight: 600,
                              py: 1.2,
                              borderRadius: 999,
                              textTransform: 'none',
                              fontSize: 15,
                              boxShadow: 'none',
                              '&:hover': { bgcolor: '#1d3996', boxShadow: 'none' }
                            }}
                          >
                            Verify Code
                          </Button>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                      <Typography variant="body2" align="center" sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 }, width: "100%", color: 'text.secondary' }}>
                        &mdash; OR &mdash;
                      </Typography>
                      <Typography variant="body2" align="center" sx={{ textAlign: "center", fontSize: { xs: 13, sm: 14 }, fontWeight: 600, width: "100%", color: 'text.primary', mb: 1 }}>
                        Verify via Unique LU ID
                      </Typography>
                      <Box sx={{ width: '100%' }}>
                        <Inputfield loadingInput={loadingInput} setLoadingInput={setLoadingInput} socket={socket} onLuError={() => openLuPopup("failure")} />
                      </Box>
                    </Box>

                    {/* App Store Buttons */}
                    <Box sx={{ width: '100%', gap: 1.25, mt: 1 }}>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ textAlign: "center", fontSize: { xs: 12, sm: 14 }, lineHeight: 1.45, width: "100%", color: 'text.secondary', mb: 1 }}
                      >
                        New to Legitimately U? Download the app to secure your account with biometric-first verification.
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          width: "fit-content",
                          maxWidth: "100%",
                          mx: "auto",
                        }}
                      >
                        <IconButton
                          sx={{
                            p: { xs: 0.75, sm: 1 },
                            borderRadius: 1.25,
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "#000",
                            "&:hover": { transform: "scale(1.05)" },
                          }}
                        >
                          <Box component="img" src={playstore} alt="Google Play Store" sx={{ width: { xs: 84, sm: 100 }, height: "auto", display: "block" }} />
                        </IconButton>
                        <IconButton
                          sx={{
                            p: { xs: 0.75, sm: 1 },
                            borderRadius: 1.25,
                            border: 1,
                            borderColor: "divider",
                            bgcolor: "#000",
                            "&:hover": { transform: "scale(1.05)" },
                          }}
                        >
                          <Box component="img" src={appstore} alt="Apple App Store" sx={{ width: { xs: 84, sm: 100 }, height: "auto", display: "block" }} />
                        </IconButton>
                      </Box>
                    </Box>

                  </Stack>
                </Box>
              ) : (
                <Stack spacing={2} sx={{ alignItems: "center", py: { xs: 1.5, sm: 2.5 }, width: "100%" }}>
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={data?.user?.profileImage || dummyuser}
                      sx={{ width: { xs: 96, sm: 120 }, height: { xs: 96, sm: 120 }, border: "4px solid #f0f0f0", boxShadow: "0 0 20px rgba(0,0,0,0.1)" }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 5,
                        right: 5,
                        bgcolor: "#52c41a",
                        color: "#fff",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                        fontWeight: "bold",
                        border: "4px solid #fff",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                      }}
                    >
                      ✓
                    </Box>
                  </Box>
                  <Typography variant="h5" align="center" sx={{ fontWeight: 800, textAlign: "center" }}>
                    Identity Confirmed. Welcome Back, <strong>{data?.user?.fullName}</strong>!
                  </Typography>
                  <Typography color="text.secondary" align="center" sx={{ textAlign: "center" }}>
                    Your identity has been verified. You are now in a secure biometric session.
                  </Typography>
                </Stack>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default QrLogin;
